import { useEffect, useRef } from 'react';
import { useSettingsStore } from '@/api/bi/settings';
import { applyServerDefaultsToFilters } from './filters';

/**
 * Bridge between the server-backed settings blob and the local filter store.
 *
 * `useFilters` reads `storage.getPrefs()` at module import — *before* the
 * per-user namespace is set by `AuthProvider` and *before* the bootstrap
 * GET on `/api/bi/settings/` resolves. As a result the very first paint
 * always uses the global defaults (`range='7d'`, `compareMode='previous-period'`).
 *
 * Once the settings store flips `hydrated=true` we re-apply the server
 * `prefs.default_range` / `compare_mode` exactly once, taking care not to
 * stomp on a `?range=` deep-link the user actually opened.
 */
export function SettingsHydrator() {
  const hydrated = useSettingsStore((s) => s.hydrated);
  const blob = useSettingsStore((s) => s.blob);
  const appliedRef = useRef(false);

  useEffect(() => {
    if (!hydrated || appliedRef.current) return;
    appliedRef.current = true;
    applyServerDefaultsToFilters({
      prefs: (blob.prefs ?? null) as
        | {
            default_range?: 'today' | 'yesterday' | 'this-week' | 'last-week' | '7d' | '30d' | 'mtd' | null;
            default_compare?: 'previous-period' | 'previous-year' | 'none' | null;
          }
        | null
        | undefined ?? undefined,
      compare_mode: (blob.compare_mode ?? null) as
        | 'previous-period'
        | 'previous-year'
        | 'none'
        | null,
    });
  }, [hydrated, blob]);

  return null;
}
